import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';
import fileFill from '@iconify/icons-eva/file-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { motion } from 'framer-motion';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import {
  Box,
  Link,
  Stack,
  Paper,
  Button,
  Typography,
  Tooltip,
} from '@material-ui/core';
// utils
import { fData } from '../../utils/formatNumber';
//
import { MIconButton } from '../@material-extend';
import { varFadeInRight } from '../animate';
import { UploadIllustration } from '../../assets';
import { GetAppOutlined } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useRef } from 'react';
import {
  downloadImage,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
} from 'src/Helpers/FileDownloadUtils';
// ----------------------------------------------------------------------

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
  [theme.breakpoints.up('md')]: { textAlign: 'left', flexDirection: 'row' }
}));

// ----------------------------------------------------------------------

UploadSingleImage.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onRemove: PropTypes.func,
  onDrop: PropTypes.func,
  sx: PropTypes.object,
  allowDownload: PropTypes.bool,
};

export default function UploadSingleImage({
  error,
  showPreview = false,
  file,
  onRemove,
  onDrop,
  sx,
  allowDownload = false,
  ...other
}) {
  const { enqueueSnackbar } = useSnackbar();
  const hasFile = !!file;
  const fileInputRef = useRef(null);

  let canDownload = false;
  if (allowDownload && hasFile) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isClient = user?.isClient || user?.isclient;
      const userType = isClient ? 3 : user?.user_type;
      canDownload = !isClient && userType !== 3;
    } catch {
      canDownload = false;
    }
  }

  const resolveFileUrl = () => {
    if (!file) return '';
    if (isString(file)) return file;
    return getAttachmentDownloadUrl(file) || file.preview || '';
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const fileUrl = resolveFileUrl();
    if (!fileUrl) {
      enqueueSnackbar('No file to download', { variant: 'warning' });
      return;
    }
    const displayName = isString(file) ? null : getAttachmentDisplayName(file);
    downloadImage(
      fileUrl,
      displayName,
      0,
      () => enqueueSnackbar('File downloaded successfully', { variant: 'success' }),
      () => enqueueSnackbar('Failed to download file', { variant: 'error' })
    );
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    ...other,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (!file && acceptedFiles.length > 0) {
        onDrop(acceptedFiles[0]);
      }
    }
  });

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
      }}
    >
      {fileRejections?.map(({ file, errors }) => {
        const { path, size } = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {!hasFile && (
        <DropZoneStyle
          {...getRootProps()}
          sx={{
            ...(isDragActive && { opacity: 0.72 }),
            ...((isDragReject || error) && {
              color: 'error.main',
              borderColor: 'error.light',
              bgcolor: 'error.lighter'
            })
          }}
        >
          <input {...getInputProps()} />
          <UploadIllustration sx={{ width: 220 }} />

          <Box sx={{ p: 3, ml: { md: 2 } }}>
            <Typography gutterBottom variant="h5">
              Drop or Select file
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Drop a file here or click&nbsp;
              <Link underline="always">browse</Link>&nbsp;through your machine
            </Typography>
          </Box>
        </DropZoneStyle>
      )}

      {fileRejections?.length > 0 && <ShowRejectionItems />}

      {hasFile && (
        <Box sx={{ my: 3 }}>
          <Box
            component={motion.div}
            {...varFadeInRight}
            sx={{
              width: 100,
              height: 100,
              borderRadius: 1.5,
              overflow: 'hidden',
              position: 'relative',
              display: 'inline-block',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              alt="file preview"
              src={isString(file) ? file : file.preview || resolveFileUrl()}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            {canDownload && (
              <Box
                className="upload-single-image-action"
                sx={{ top: 6, left: 6, position: 'absolute', zIndex: 1 }}
              >
                <Tooltip title="Download">
                  <MIconButton
                    size="small"
                    onClick={handleDownload}
                    sx={{
                      p: '2px',
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                      },
                    }}
                  >
                    <GetAppOutlined fontSize="small" />
                  </MIconButton>
                </Tooltip>
              </Box>
            )}
            <Box
              className="upload-single-image-action"
              sx={{ top: 6, right: 6, position: 'absolute', zIndex: 1 }}
            >
              <MIconButton
                size="small"
                onClick={onRemove}
                sx={{
                  p: '2px',
                  color: 'common.white',
                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                  },
                }}
              >
                <Icon icon={closeFill} />
              </MIconButton>
            </Box>
          </Box>
        </Box>
      )}

      {hasFile && (
        <Stack direction="row" justifyContent="flex-end">
          <Button onClick={onRemove} sx={{ mr: 1.5 }}>
            Remove
          </Button>
          {/* <Button onRemove {...getRootProps()} variant="contained">Upload new file</Button> */}
        </Stack>
      )}
    </Box>
  );
}
